<script module lang="ts">
  import { writable } from "svelte/store";

  import { analytics } from "$lib/analytics";

  export function useCopy({
    analyticsEventName,
    pageName,
  }: { analyticsEventName?: string; pageName?: string } = {}) {
    let lastPress = writable<number | null>(null);

    const handleCopy = async function (source: string) {
      if (!source) return;

      if (analyticsEventName) {
        analytics.track(analyticsEventName, { pageName });
      }

      await navigator.clipboard.writeText(source);
      lastPress.set(Date.now());

      const resetTimeout = setTimeout(() => {
        lastPress.set(null);
      }, 1000);

      return () => clearTimeout(resetTimeout);
    };

    return {
      handleCopy,
      lastPress,
    };
  }
</script>

<script lang="ts">
  import clsx from "clsx";
  import { Check, Copy } from "lucide-svelte";

  import tooltip from "$lib/tooltip";

  type CopyButtonVariant = "default" | "minimal" | "minimal-code-sample";

  export let source: string;
  export let page: string = "";
  export let variant: CopyButtonVariant = "default";
  export let size: number = 16;
  export let tooltipText: string = "Copy";
  export let analyticsEventName: string | undefined = undefined;

  // Default buttons don't need a tooltip because they already have a label
  let action = variant === "default" ? () => {} : tooltip;

  const { handleCopy, lastPress } = useCopy({
    analyticsEventName,
    pageName: page,
  });

  const variants = {
    "minimal-code-sample": "rounded-r border bg-dark-gray p-1",
    minimal: "rounded p-1",
    default: "rounded border bg-dark-gray p-1",
  };

  $: state =
    $lastPress !== null
      ? "text-c-green-100 border-c-green-100"
      : "text-c-gray-80 border-c-gray-20";

  const icon = {
    "minimal-code-sample": "mx-2",
    default: "ml-0.5",
    minimal: "text-c-gray-80 group-hover:text-c-gray-90 transition-colors",
  };
</script>

<button
  class={clsx(
    "flex items-center leading-5 group outline-c-green-100/50",
    variants[variant],
    state,
  )}
  on:click|stopPropagation={() => handleCopy(source)}
  use:action={tooltipText}
>
  {#if $lastPress === null}
    <Copy class={icon[variant]} {size} />
    {#if variant === "default"}
      <span class="ml-1.5 pr-1 text-xs font-sans">Copy</span>
    {/if}
  {:else}
    <Check class={icon[variant]} {size} />
    {#if variant === "default"}
      <span class="ml-1.5 pr-1 text-xs font-sans">Copied</span>
    {/if}
  {/if}
</button>
